/**
 * Shipping insurance — keeps a tier-priced insurance line in sync with the cart.
 *
 * Config comes from snippets/shipping-insurance-config.liquid, UI from
 * snippets/shipping-insurance.liquid.
 *
 * Rendering is delegated to <cart-drawer-v2>: after a successful insurance
 * mutation the module hands the fresh cart to drawer.renderFromExternal(cart,
 * SOURCE), and the drawer re-renders itself through its own Section Rendering API
 * fetch exactly as it does for every other cart mutation. The SOURCE tag is what
 * stops the drawer's own `cart-drawer:updated` event from bouncing back in here
 * and starting another sync.
 *
 * On the /cart page there is no drawer, so the mutation lands without a
 * re-render and the page is reloaded instead — Impulse's cart page renders its
 * totals server-side and has no partial-refresh hook for a third line item.
 *
 * Exposes:
 *   window.ShippingInsurance  = { sync(immediate), flush() }
 *   window.CartDrawerFilters  = { isInsuranceItem, merchandiseCount,
 *                                 merchandiseSubtotalCents, cartAppearsEmpty }
 */
(function () {
  'use strict';

  const SOURCE = 'shipping-insurance';
  const ON_CHANGE_DEBOUNCE_TIMER = 300;
  const CHECKOUT_FLUSH_TIMEOUT = 2500;
  // How long to ignore Impulse's own `cart:updated` after we ask it to rebuild.
  // theme.CartForm fires that at the end of the rebuild it does on our behalf,
  // and reacting to it would start a second sync for a cart we just settled.
  const REBUILD_SETTLE_TIMER = 600;
  // The custom drawer's checkout control is a plain <a href="/checkout">, not a
  // form submit button. The other two selectors cover Impulse's cart page form
  // and any other form-backed checkout button in the theme.
  const CHECKOUT_SELECTOR = 'a.cartv2__checkout-btn, button[name="checkout"], #checkout';
  const CHECKOUT_LOADING_CLASS = 'shipping-insurance-btn-loading';

  let syncInProgress = false;
  let pendingSync = false;
  let debounceTimer = null;
  let debounceScheduled = false;
  let currentSync = Promise.resolve();
  let toggleDelegationBound = false;
  let checkoutSubmitting = false;
  let rebuildingCart = false;

  /* ------------------------------------------------------------------ config */

  // Read on every call: shipping-insurance-config.liquid may be parsed after this file.
  function getConfig() {
    const config = window.shippingInsuranceConfig;
    return config && config.enabled ? config : null;
  }

  /* ------------------------------------------------------------------ routes */

  /**
   * The root already ends in a slash on every storefront that sets it, but it is
   * normalised here so a root without one cannot produce `/encart.js` and one
   * with a trailing slash cannot produce `//cart.js`.
   */
  function rootPath() {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.charAt(root.length - 1) === '/' ? root : root + '/';
  }

  function cartUrl() {
    return rootPath() + 'cart.js';
  }

  function cartAddUrl() {
    return rootPath() + 'cart/add.js';
  }

  function cartChangeUrl() {
    return rootPath() + 'cart/change.js';
  }

  function cartUpdateUrl() {
    return rootPath() + 'cart/update.js';
  }

  function fetchConfig() {
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' }
    };
  }

  /* -------------------------------------------------------------- cart state */

  // Cart JSON carries no product tags, so lines are matched by property, variant
  // or product only. The tag check lives on the Liquid side.
  function isInsuranceItem(item, config) {
    if (!item) return false;

    if (item.properties && item.properties[config.propertyKey]) return true;
    if ((config.variantIds || []).indexOf(item.id) !== -1) return true;

    return config.productId > 0 && item.product_id === config.productId;
  }

  /**
   * /cart/add.js returns only the items it added and has no `attributes` key, so
   * it is not a cart. Treating it as one makes the toggle uncheck itself after
   * add-to-cart, because the missing attribute reads as "opted out".
   */
  function isUsableCartState(state) {
    return !!state && Array.isArray(state.items) && 'attributes' in state;
  }

  async function fetchCart() {
    const response = await fetch(cartUrl(), {
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    });
    return response.json();
  }

  async function postCart(url, body) {
    const response = await fetch(url, { ...fetchConfig(), body: JSON.stringify(body) });
    const parsed = await response.json();

    if (parsed && parsed.status && Number(parsed.status) >= 400) {
      throw new Error(parsed.description || parsed.message || 'Cart request failed');
    }
    return parsed;
  }

  function summarize(cart, config) {
    const lines = [];
    let merchandiseCount = 0;
    let merchandiseSubtotalCents = 0;

    (cart.items || []).forEach((item) => {
      if (isInsuranceItem(item, config)) {
        lines.push(item);
      } else {
        merchandiseCount += item.quantity;
        merchandiseSubtotalCents += item.final_line_price;
      }
    });

    return {
      lines: lines,
      merchandiseCount: merchandiseCount,
      merchandiseSubtotalCents: merchandiseSubtotalCents,
      optedOut: (cart.attributes || {})[config.optOutAttribute] === 'true'
    };
  }

  /**
   * Cheapest ladder variant priced at or above `priceCents`, falling back to the
   * dearest one when the fee overshoots the ladder — a cart beyond the top variant
   * is charged that ceiling rather than losing its cover entirely.
   *
   * `priceVariants` deliberately excludes the flat $0.99 variant (see
   * snippets/shipping-insurance-config.liquid), so a percentage fee can never
   * resolve onto the flat rung.
   */
  function resolveVariantForPrice(config, priceCents) {
    let above = null;
    let dearest = null;

    (config.priceVariants || []).forEach((pair) => {
      if (pair[0] >= priceCents && (above === null || pair[0] < above[0])) above = pair;
      if (dearest === null || pair[0] > dearest[0]) dearest = pair;
    });

    const chosen = above || dearest;
    return chosen ? chosen[1] : null;
  }

  /**
   * The whole pricing rule, hardcoded — no tier settings are involved:
   *
   *   basis <  thresholdCents ($100)  ->  the flat variant ($0.99)
   *   basis >= thresholdCents         ->  percentRate (2%) of the basis
   *
   * Math.ceil, not Math.round, twice over: the percentage is rounded up to the
   * whole cent, then resolveVariantForPrice rounds up again to the next real
   * variant price. So the fee is never below the stated percentage and never more
   * than one ladder rung above it. snippets/shipping-insurance.liquid computes the
   * displayed fee the same way, so the two cannot drift.
   */
  function resolveFeeVariantId(config, basisCents) {
    if (basisCents <= 0) return null;

    if (basisCents < config.thresholdCents) return config.baseVariantId || null;

    return resolveVariantForPrice(config, Math.ceil((basisCents * config.percentRate) / 100));
  }

  /* ----------------------------------------------------------------- theme IO */

  function getDrawer() {
    const drawer = document.querySelector('cart-drawer-v2');
    return drawer && typeof drawer.renderFromExternal === 'function' ? drawer : null;
  }

  const RELOAD_GUARD_KEY = 'shippingInsuranceReloaded';

  /**
   * Hand the fresh cart to the drawer so it re-renders through its own section
   * fetch, tagged as ours so its `cart-drawer:updated` event is ignored below.
   *
   * With no drawer on the page — the /cart template, where sections/header.liquid
   * deliberately renders none — there is nothing to re-render in place: Impulse
   * renders the cart page's line items, subtotal and this widget's price
   * server-side. Reload so all three agree with the cart that was just mutated.
   * Skipped while checkout is in flight, so a reload cannot race the navigation
   * away from the page.
   *
   * The sessionStorage guard bounds that to one reload per visit to the page.
   * A settled cart reaches runSync's early returns and never gets here, so a
   * second reload means the sync is not converging — a tier that keeps flapping,
   * or a variant the cart refuses. Reloading on a loop would make /cart unusable,
   * so the stale price is the better failure. The flag is cleared by
   * clearReloadGuard() on every sync that ends without a mutation.
   */
  /**
   * Impulse's own cart UI. Both the stock drawer and the cart page render their
   * line items from templates/cart.ajax.liquid into this container and rebuild it
   * on `cart:build` (theme.CartForm in assets/theme.js).
   */
  function getImpulseCartContainer() {
    return document.querySelector('[data-products]');
  }

  function formatMoney(cents) {
    if (window.theme && theme.Currency && typeof theme.Currency.formatMoney === 'function') {
      return theme.Currency.formatMoney(cents, theme.settings && theme.settings.moneyFormat);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  /**
   * Rewrites the fee's price label from the cart we just settled.
   *
   * The label is server-rendered Liquid, and Impulse's rebuild only replaces
   * [data-products] — it never touches this widget, which sits in the footer. So
   * on a tier change the label would otherwise keep advertising the previous
   * band's price. The custom drawer does not need this: it re-renders its whole
   * footer through the Section Rendering API.
   */
  function updateWidgetPrice(cart) {
    const config = getConfig();
    if (!config || !cart || !Array.isArray(cart.items)) return;

    let cents = 0;
    let merchandise = 0;

    cart.items.forEach(function (item) {
      if (isInsuranceItem(item, config)) {
        cents += item.final_line_price;
      } else {
        merchandise += item.quantity;
      }
    });

    getWidgets().forEach(function (widget) {
      // The snippet renders the widget on every page whether or not the cart
      // holds anything, because the stock drawer's footer is never re-rendered.
      // Showing and hiding it is therefore this module's job.
      widget.hidden = merchandise === 0;

      const label = widget.querySelector('.shipping-insurance__price');
      if (!label) return;

      label.textContent = cents > 0 ? formatMoney(cents) : '';
      label.hidden = cents <= 0;
    });
  }

  async function renderDrawer(cart) {
    const drawer = getDrawer();
    if (drawer) {
      await drawer.renderFromExternal(cart, SOURCE);
      return;
    }

    // Impulse's own cart UI is on the page (stock drawer or cart page): ask it to
    // rebuild rather than reloading. `cart:build` re-fetches cart.ajax markup and
    // refreshes the rows, the item count and the subtotal in place, which keeps an
    // open drawer open — a reload would slam it shut mid-interaction.
    if (getImpulseCartContainer()) {
      updateWidgetPrice(cart);
      rebuildingCart = true;
      document.dispatchEvent(new CustomEvent('cart:build'));
      setTimeout(function () {
        rebuildingCart = false;
      }, REBUILD_SETTLE_TIMER);
      return;
    }

    // Nothing on the page can re-render itself. Only the cart template is worth
    // reloading for; anywhere else the shopper sees no cart markup anyway.
    if (!document.body.classList.contains('template-cart') || checkoutSubmitting) return;

    try {
      if (sessionStorage.getItem(RELOAD_GUARD_KEY)) {
        console.warn('Shipping insurance: skipping a second cart page reload; the fee shown may be stale.');
        return;
      }
      sessionStorage.setItem(RELOAD_GUARD_KEY, '1');
    } catch (error) {
      // Private mode / storage disabled: fall through and reload. runSync
      // converges in one pass, so the unguarded path is the normal one anyway.
    }

    window.location.reload();
  }

  function clearReloadGuard() {
    try {
      sessionStorage.removeItem(RELOAD_GUARD_KEY);
    } catch (error) {
      /* storage disabled — nothing to clear */
    }
  }

  /* ---------------------------------------------------------------------- UI */

  function getWidgets() {
    return document.querySelectorAll('[data-shipping-insurance]');
  }

  function setSyncingUI(isSyncing) {
    document.body.toggleAttribute('data-insurance-syncing', isSyncing);

    getWidgets().forEach((widget) => {
      widget.setAttribute('data-syncing', isSyncing ? 'true' : 'false');
      widget.setAttribute('aria-busy', isSyncing ? 'true' : 'false');

      const toggle = widget.querySelector('[data-insurance-toggle]');
      if (toggle) toggle.disabled = isSyncing;
    });
  }

  function showError(message) {
    getWidgets().forEach((widget) => {
      const target = widget.querySelector('[data-insurance-error]');
      if (target) target.textContent = message || '';
    });
  }

  /**
   * Drives the toggle and the two-state copy together. The state attribute is what
   * CSS keys the icon/title/subtitle variants off, so flipping it here swaps them
   * immediately — the shopper does not wait on the cart round-trip — and every
   * caller (optimistic flip, failed-request revert, post-render reconcile) stays
   * in step. The label follows the visible title, so the switch is never announced
   * with copy that is no longer on screen.
   */
  function setToggleState(checked) {
    const state = checked ? 'protected' : 'unprotected';

    getWidgets().forEach((widget) => {
      widget.setAttribute('data-insurance-state', state);

      const toggle = widget.querySelector('[data-insurance-toggle]');
      if (!toggle) return;

      toggle.checked = checked;
      toggle.setAttribute('aria-checked', checked ? 'true' : 'false');

      const label = widget.getAttribute(`data-label-${state}`);
      if (label) toggle.setAttribute('aria-label', label);
    });
  }

  /** Re-point the toggle at the cart attribute after the DOM is replaced. */
  async function reconcileToggles(state) {
    const config = getConfig();
    if (!config) return;

    const cart = isUsableCartState(state) ? state : await fetchCart();
    setToggleState((cart.attributes || {})[config.optOutAttribute] !== 'true');
    // Also covers mutations we did not make — an emptied cart has to hide the
    // widget even though no insurance sync was involved.
    updateWidgetPrice(cart);
  }

  /* -------------------------------------------------------------------- sync */

  /**
   * Sets a cart line's quantity, addressed by its line item key rather than its
   * position.
   *
   * A line number is only meaningful for the exact cart it was read from. /cart/add
   * .js appends, so it does not disturb lower numbers on its own — but any other
   * mutation landing in between (the shopper removing an item, a second tab, an
   * app) renumbers the lines, and the change then hits whichever line now sits in
   * that slot instead of the one it was aiming at.
   *
   * Keys are stable across all of that.
   */
  function setLineQuantity(item, quantity) {
    return postCart(cartChangeUrl(), { id: item.key, quantity: quantity });
  }

  /** Drops a cart line. Keyed, via setLineQuantity. */
  function removeLine(item) {
    return setLineQuantity(item, 0);
  }

  async function runSync() {
    const config = getConfig();
    if (!config) return;

    let cart = await fetchCart();
    let state = summarize(cart, config);

    // Collapse duplicates before anything else, then re-read the cart and fix
    // whatever survived. Keyed removal makes the order irrelevant.
    if (state.lines.length > 1) {
      for (let i = 1; i < state.lines.length; i++) {
        await removeLine(state.lines[i]);
      }
      cart = await fetchCart();
      state = summarize(cart, config);
    }

    // Merchandise only: the fee is never charged on itself.
    const basisCents = state.merchandiseSubtotalCents;

    const targetVariantId = resolveFeeVariantId(config, basisCents);
    const wantsInsurance = state.merchandiseCount > 0 && !state.optedOut && !!targetVariantId;
    const existing = state.lines[0] || null;

    if (!wantsInsurance) {
      // Nothing to change: the cart is settled, so release the reload guard.
      if (!existing) {
        clearReloadGuard();
        return cart;
      }

      const updated = await removeLine(existing);
      await renderDrawer(updated);
      return updated;
    }

    if (existing && String(existing.id) === String(targetVariantId)) {
      // The line is a fee, so it is always exactly one unit — never a quantity the
      // shopper gets to pick. Two live paths produce more: the insurance product is
      // published, so its own /products/… add-to-cart form works, and /cart is
      // mutable through /cart/change.js (and through a stale tab) even with the row
      // hidden. Comparing only the variant let those stand forever, and the widget
      // agreed with them because it prints the whole line total.
      //
      // The variant is already right, so only the quantity moves; the next sync
      // then reads quantity 1 and returns here instead.
      if (Number(existing.quantity) === 1) {
        // Nothing to change: the cart is settled, so release the reload guard.
        clearReloadGuard();
        return cart;
      }

      const updated = await setLineQuantity(existing, 1);
      await renderDrawer(updated);
      return updated;
    }

    const addBody = {
      items: [
        {
          id: Number(targetVariantId),
          quantity: 1,
          properties: { [config.propertyKey]: 'true' }
        }
      ]
    };

    if (!existing) {
      await postCart(cartAddUrl(), addBody);

      // /cart/add.js returns only the added items, so re-read the cart before
      // handing it to the drawer.
      const cartJson = await fetchCart();
      await renderDrawer(cartJson);
      return cartJson;
    }

    // Tier change. Shopify's cart cannot repoint a line at another variant, so the
    // new tier is added and the old line removed by key. Only the second request's
    // result is rendered so the shopper sees a single re-render, and because the
    // line is hidden its move to the end of the cart is invisible.
    await postCart(cartAddUrl(), addBody);
    const updated = await removeLine(existing);
    await renderDrawer(updated);
    return updated;
  }

  async function processSync() {
    // syncInProgress must be cleared in a finally — a throw that leaves it set
    // would wedge every future sync for the life of the page.
    syncInProgress = true;
    setSyncingUI(true);

    let cart = null;

    try {
      cart = await runSync();
      showError('');
    } catch (error) {
      // Surfaced, not just logged: a swallowed failure here leaves the widget
      // advertising a fee the cart does not hold, which reads as the price simply
      // refusing to move.
      console.error('Shipping insurance sync failed:', error);
      const config = getConfig();
      if (config) showError(config.strings.error);
    } finally {
      syncInProgress = false;
      setSyncingUI(false);
    }

    if (pendingSync) {
      pendingSync = false;
      return processSync();
    }

    // Rendering above replaced the widget markup, so re-point the toggle at the
    // cart attribute. Reuses the cart we already hold rather than refetching.
    await reconcileToggles(cart);
  }

  function startSync() {
    if (syncInProgress) {
      pendingSync = true;
      return currentSync;
    }

    currentSync = processSync();
    return currentSync;
  }

  function scheduleSync(immediate) {
    if (!getConfig()) return currentSync;

    clearTimeout(debounceTimer);

    if (immediate) {
      debounceScheduled = false;
      return startSync();
    }

    debounceScheduled = true;
    debounceTimer = setTimeout(function () {
      debounceScheduled = false;
      startSync();
    }, ON_CHANGE_DEBOUNCE_TIMER);

    return currentSync;
  }

  /** Settle any outstanding work. A debounced sync is run now, not discarded. */
  function flush() {
    if (!getConfig()) return Promise.resolve();

    if (debounceScheduled) {
      clearTimeout(debounceTimer);
      debounceScheduled = false;
      return startSync();
    }

    return currentSync;
  }

  /* ------------------------------------------------------------------ toggle */

  async function onToggleChange(event) {
    const toggle = event.target.closest('[data-insurance-toggle]');
    if (!toggle) return;

    const config = getConfig();
    if (!config) return;

    const previous = !toggle.checked;
    const optOut = !toggle.checked;

    // Swaps the copy and icon in the same frame as the knob; reverted below if the
    // cart request fails, so the widget never claims cover the cart does not hold.
    setToggleState(toggle.checked);
    showError('');
    setSyncingUI(true);

    try {
      await postCart(cartUpdateUrl(), {
        attributes: { [config.optOutAttribute]: optOut ? 'true' : '' }
      });
      await scheduleSync(true);
    } catch (error) {
      console.error('Shipping insurance toggle failed:', error);
      setToggleState(previous);
      showError(config.strings.error);
    } finally {
      setSyncingUI(false);
    }
  }

  /**
   * One delegated listener, because the drawer replaces its body/footer innerHTML
   * on every cart mutation. A listener bound to the input itself is orphaned by
   * that swap — the toggle then still flips visually via CSS :checked but fires no
   * cart logic at all.
   */
  function bindToggleDelegation() {
    if (toggleDelegationBound) return;

    document.addEventListener('change', onToggleChange);
    toggleDelegationBound = true;
  }

  /* ---------------------------------------------------------------- checkout */

  function timeoutPromise(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * The drawer's checkout control is `<a href="/checkout">`, so there is no form
   * to submit — the click is intercepted, the pending insurance work is flushed,
   * and the browser is then navigated by hand. `button[name="checkout"]` /
   * `#checkout` keep working for the cart page's form-backed checkout button.
   *
   * Capture phase, so this runs before the drawer's own document-level click
   * handler.
   */
  function bindCheckoutInterception() {
    document.addEventListener(
      'click',
      function (event) {
        const checkoutEl = event.target.closest(CHECKOUT_SELECTOR);
        if (!checkoutEl || !getConfig()) return;

        // Second click while the first is still flushing: swallow it rather than
        // letting it start a competing navigation.
        if (checkoutSubmitting) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }

        const form = checkoutEl.form || null;

        // A form-backed button can sit in an invalid form — the cart page's quantity
        // inputs carry min/max/step. Bail out and let the browser report it
        // natively; intercepting would swallow the click and leave a dead button.
        if (form && typeof form.checkValidity === 'function' && !form.checkValidity()) return;

        event.preventDefault();
        event.stopPropagation();
        checkoutSubmitting = true;
        checkoutEl.classList.add(CHECKOUT_LOADING_CLASS);

        // A stalled cart request must never block checkout, so the flush races a
        // hard timeout and the shopper is sent to checkout either way.
        Promise.race([
          Promise.resolve(flush()).catch(function () {}),
          timeoutPromise(CHECKOUT_FLUSH_TIMEOUT)
        ]).then(function () {
          if (checkoutEl.tagName === 'A') {
            window.location.href = checkoutEl.getAttribute('href') || '/checkout';
            return;
          }

          if (form && typeof form.requestSubmit === 'function') {
            // Submit with the button as submitter so its name="checkout" is in the
            // form entry list. Disabling it first would drop the submitter and post
            // to /cart instead of /checkout — hence checkoutSubmitting above rather
            // than pre-disabling.
            form.requestSubmit(checkoutEl);
          } else if (form) {
            form.submit();
          } else {
            window.location.href = '/checkout';
          }
        });
      },
      true
    );
  }

  /* ----------------------------------------------------------- cart subscribe */

  /**
   * Three sources, because two different cart UIs can own the page.
   *
   * `cart-drawer:updated` is <cart-drawer-v2>'s own event, carrying { cart,
   * source }; mutations tagged with our SOURCE are skipped or we would loop.
   *
   * `cart:updated` is Impulse's, dispatched by theme.CartForm after every
   * quantity change it handles — which is the only thing that happens on /cart,
   * where no drawer is rendered. Without this the fee would never re-tier there:
   * dropping a $148 cart to $74 would keep charging the 2% band. Our own
   * mutations never go through CartForm, so this cannot feed back on itself.
   *
   * `ajaxProduct:added` covers add-to-cart from anywhere on the cart page
   * (recommendations, quick add), which CartForm does not announce.
   */
  function bindCartUpdateSubscription() {
    document.addEventListener('cart-drawer:updated', function (event) {
      const detail = (event && event.detail) || {};
      if (detail.source === SOURCE) return; // our own mutation — ignore or we loop

      reconcileToggles(detail.cart);
      scheduleSync(false);
    });

    document.addEventListener('cart:updated', function (event) {
      // Ours: theme.CartForm fires this at the end of the rebuild renderDrawer
      // just asked for. Reacting would sync a cart that is already settled.
      if (rebuildingCart) return;

      const detail = (event && event.detail) || {};

      reconcileToggles(detail.cart);
      scheduleSync(false);
    });

    // Same `detail.product` success test as the drawer: theme.AjaxProduct fires
    // this on its 422 branch with no detail, and a failed add leaves the cart
    // untouched, so there is nothing to re-tier.
    document.addEventListener('ajaxProduct:added', function (event) {
      if (!event.detail || !event.detail.product) return;

      scheduleSync(false);
    });
  }

  /* -------------------------------------------------------------------- init */

  /**
   * The feature has been switched off in theme settings, but a cart from before
   * the switch was flipped can still hold a fee line. No widget renders now, so
   * nothing would ever take it out again and the shopper would be charged for
   * cover the store no longer offers. Strip every insurance line and redraw.
   *
   * Line detection does not depend on `enabled`, so the disabled config still
   * recognises its own lines.
   */
  async function removeInsuranceLines(config) {
    try {
      const cart = await fetchCart();
      const lines = (cart.items || []).filter((item) => isInsuranceItem(item, config));
      if (!lines.length) return;

      let updated = null;
      for (let i = 0; i < lines.length; i++) {
        updated = await removeLine(lines[i]);
      }

      await renderDrawer(updated);
    } catch (error) {
      // Nothing to surface: with the feature off there is no widget to show an
      // error in. The next page load tries again.
      console.error('Shipping insurance cleanup failed:', error);
    }
  }

  function init() {
    // Read the raw object, not getConfig(): that returns null while the feature is
    // switched off, and the off state still has the cleanup below to run.
    const config = window.shippingInsuranceConfig;
    if (!config) return;

    if (!config.enabled) {
      if (config.cartItemCount > 0) removeInsuranceLines(config);
      return;
    }

    bindToggleDelegation();
    bindCheckoutInterception();
    bindCartUpdateSubscription();

    // Any non-empty cart syncs on load. Keyed off the raw Shopify count rather
    // than the widget's presence, so a cart left holding only a stale insurance
    // line — which renders as empty, widget included — still gets cleaned up.
    if (config.cartItemCount > 0) scheduleSync(true);
  }

  window.ShippingInsurance = {
    sync: scheduleSync,
    flush: flush
  };

  /**
   * Helpers for hiding the shipping insurance line from cart counts and totals.
   *
   * The insurance line is a real product line item, so every surface that counts
   * items or sums money has to skip it. Config is read on each call rather than
   * captured once — shipping-insurance-config.liquid may be parsed after this file.
   *
   * Cart JSON line items carry no product tags, so lines are matched by property
   * key, variant ID or product ID only (the tag is used on the Liquid side).
   *
   * Defined unconditionally, outside init(), so assets/cart-drawer-v2.js can call
   * it even when insurance is switched off. With no config, or insurance disabled,
   * every helper returns the untouched cart value so behaviour is identical to
   * having no insurance feature at all.
   */
  window.CartDrawerFilters = {
    get config() {
      const config = window.shippingInsuranceConfig;
      return config && config.enabled ? config : null;
    },

    isInsuranceItem(item) {
      const config = window.CartDrawerFilters.config;
      if (!config || !item) return false;

      return isInsuranceItem(item, config);
    },

    /** cart.item_count without insurance units. */
    merchandiseCount(cart) {
      if (!cart) return 0;
      if (!window.CartDrawerFilters.config || !Array.isArray(cart.items)) return cart.item_count;

      return cart.items.reduce((count, item) => {
        return window.CartDrawerFilters.isInsuranceItem(item) ? count : count + item.quantity;
      }, 0);
    },

    /** cart.items_subtotal_price without the insurance line. */
    merchandiseSubtotalCents(cart) {
      if (!cart) return 0;
      if (!window.CartDrawerFilters.config || !Array.isArray(cart.items)) return cart.items_subtotal_price;

      return cart.items.reduce((total, item) => {
        return window.CartDrawerFilters.isInsuranceItem(item) ? total : total + item.final_line_price;
      }, 0);
    },

    /** A cart holding nothing but insurance has to read as empty. */
    cartAppearsEmpty(cart) {
      return window.CartDrawerFilters.merchandiseCount(cart) === 0;
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
